import Vue from 'vue';
import {IUserData} from '@/models/ApiModels';
import {IActionsSignature, IUserDataStore} from "@/models/StoreModels";
import {login, register, registerWithVCode, refreshUserData, sendResetSms, validate} from "@/services/loginManager";
import {ILogin, IRegister, ISendReset, IValidate} from "@/models/loginModels";
import {isNil} from "ramda";
import {Languages} from "@/constants/locale";
import {setI18nLanguage, getI18nLanguage, translateString, translateStringWithParams} from "@/services/languageUtils";
import * as services from "@/services/services";
import VueI18n from "vue-i18n";
import {i18n} from "@/main";

const GENERIC_USER_ID = 0;

const initialState = (): IUserDataStore => {

  return {
    id: GENERIC_USER_ID,
    first_name: "",
    last_name: "",
    delivery_token: "",
    default_hub_id: null,
    tos_accepted: null,
    advertising_consent: null,
    nif: "",
    email: "",
    phone_number: "",
    created_at: "",
    updated_at: "",
    office_email: "",
    office_location: "",
    office_phone: "",
    one_eur_earnings: 0,
    co2_saved: 0,
    sms_verified: null,
    language: getI18nLanguage(),
  };
};


const getters = {

  userIsLogged(state: IUserDataStore): boolean {
    return state.id !== GENERIC_USER_ID;
  },

  getProfile(state: IUserDataStore): IUserData {
    return state;
  },

  getId(state: IUserDataStore): number {
    return state.id;
  },

  getDefaultHubId(state: IUserDataStore): number|null {
    return state.default_hub_id;
  },

  userSmsWasValidated(state: IUserDataStore): boolean {
    return !isNil(state.sms_verified);
  },

  getFirstName(state: IUserDataStore): string {
    return state.first_name;
  },

  getLanguage(state: IUserDataStore): Languages {
    return state.language;
  },

  getEmail(state: IUserDataStore): string {
    return state.email;
  },

  getPhone(state: IUserDataStore): string {
    return state.phone_number;
  },

  getDeliveryToken(state: IUserDataStore): string {
    return state.delivery_token;
  },

  getAddressPlusDeliveryCode(state: IUserDataStore): VueI18n.TranslateResult {
    const deliveryToken = getters.getDeliveryToken(state);

    return translateStringWithParams(
      'officeAddress',
      {
        code: deliveryToken,
        address: state.office_location,
      });
  },

  getOfficePhoneNumber(state: IUserDataStore): string {
    return state.office_phone;
  },

  getOfficePhoneNumberLink(state: IUserDataStore): string {
    return "tel:" + state.office_phone;
  },

  getOfficeEmail(state: IUserDataStore): string {
    return state.office_email;
  },
};


const getInitialStateWithLastUserLanguage = () => {
  const lang = getI18nLanguage(); // last user language still stored in I18n as for now
  const state: IUserDataStore = initialState();
  state.language = lang; // this keeps the logged user language

  return state;
};

const actions = {
  reset({commit}: IActionsSignature<IUserDataStore>) {
    // on reset we still keep the last language, so we don't need to change it
    const state = getInitialStateWithLastUserLanguage();
    commit('reset', state);
  },

  setLanguage({state, dispatch, commit}: IActionsSignature<IUserDataStore>, lang: Languages) {
    // we need to ensure that the language is changed, no matter what we do
    if (state.id > 0) {
      // if the user is not the default, we need to update the backend
      return services.apiCaller.POST("/user", {
        id: state.id,
        first_name: state.first_name,
        last_name: state.last_name,
        phone_number: state.phone_number,
        email: state.email,
        language: lang,
      })
        .then((userData: IUserData) => dispatch('setUserData', userData)); // this changes the language
    }

    // if it is the default user, we will not try to update the backend
    setI18nLanguage(lang);
    return commit('setLanguage', lang); // this doesn't change the language
  },

  registerUser({commit, dispatch, state}: IActionsSignature<IUserDataStore>, userData: IRegister) {

    return register(userData)
                .then((res: IUserData) => dispatch('setUserData', res));
  },

  registerUserWithVCode({commit, dispatch, state}: IActionsSignature<IUserDataStore>, userData: IRegister) {

    return registerWithVCode(userData)
      .then((res: IUserData) => dispatch('setUserData', res));
  },

  loginUser({commit, dispatch, state}: IActionsSignature<IUserDataStore>, userData: ILogin) {

    return login(userData)
              .then((res: IUserData) => dispatch('setUserData', res));

  },

  validateUser({commit, dispatch, state}: IActionsSignature<IUserDataStore>, validateData: IValidate) {
    return validate(validateData);
  },

  sendResetPassword({commit, dispatch, state}: IActionsSignature<IUserDataStore>, userData: ISendReset) {
    return sendResetSms(userData);
  },

  setUserData({commit}: IActionsSignature<IUserDataStore>, userData: IUserData) {

    setI18nLanguage(userData.language);
    return commit('setUserData', userData);
  },

  refreshUserData({commit}: IActionsSignature<IUserDataStore>) {
    return refreshUserData()
                 .then((res: IUserData) => {
                   setI18nLanguage(res.language);
                   commit('setUserData', res);
                 });
  },
};


const mutations = {
  setUserData(state: IUserDataStore, userData: IUserData) {
    state = Object.assign(state, userData);
  },

  setLanguage(state: IUserDataStore, lang: Languages) {
    Vue.set(state, 'language', lang);
  },

  reset(state: IUserDataStore, userData: IUserDataStore) {
    state = Object.assign(state, userData);
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
